import { useActionState } from "react";
import { updateWordingAction } from "@/actions/templates";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { initialFormState } from "@/lib/formState";
import { IcuAssignmentField, type PreviewBinding } from "./IcuAssignmentField";
import styles from "./WordingForm.module.css";
import { TITLE_HINT } from "./wordingCopy";

type WordingFormProps = {
  templateId: number;
  localeCode: string;
  title: string;
  assignment: string;
  bindings: readonly PreviewBinding[];
};

// Wording is its own form because it is versioned per locale: saving the
// default locale rewrites the source every translation was made from, and drops
// each of those to NEEDS_REVIEW.
export const WordingForm = ({
  templateId,
  localeCode,
  title,
  assignment,
  bindings,
}: WordingFormProps) => {
  const [state, formAction, isPending] = useActionState(
    updateWordingAction.bind(null, templateId),
    initialFormState,
  );

  const staled = state.listValues?.staledLocales ?? [];

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>Wording · {localeCode}</h2>
      <form action={formAction} className={styles.form}>
        <TextField
          name="title"
          label="Title"
          hint={TITLE_HINT}
          placeholder="Emoji presentation"
          defaultValue={state.values.title ?? title}
          errors={state.fieldErrors.title}
        />
        <IcuAssignmentField
          name="assignment"
          label="Assignment"
          localeCode={localeCode}
          defaultValue={state.values.assignment ?? assignment}
          errors={state.fieldErrors.assignment}
          bindings={bindings}
        />
        {staled.length === 0 ? null : (
          <output className={styles.staled}>
            {staled.join(", ")} dropped to NEEDS_REVIEW — they were written
            against the wording you just replaced.
          </output>
        )}
        <div>
          <Button type="submit" variant="secondary" disabled={isPending}>
            {isPending ? "Saving..." : "Save wording"}
          </Button>
        </div>
      </form>
    </section>
  );
};
